// Pure motion maths shared by the renderer and its tests (no GL, no DOM, no clocks).
import { ease } from "./easing.ts";
import type { Look, RevealEffect, TransitionKind } from "./types.ts";

export const NEUTRAL_LOOK: Look = { bloom: 0, bloom_threshold: 0.7, bloom_radius: 1, vignette: 0, grain: 0, aberration: 0, exposure: 1, contrast: 1, saturation: 1 };

/** Look with defaults filled in (older documents have no `look`). */
export const lookOf = (l: Partial<Look> | undefined | null): Look => ({ ...NEUTRAL_LOOK, ...(l ?? {}) });
export const lookIsNeutral = (l: Look): boolean =>
  l.bloom === 0 && l.vignette === 0 && l.grain === 0 && l.aberration === 0 && l.exposure === 1 && l.contrast === 1 && l.saturation === 1;

const clamp01 = (x: number): number => (x > 0 ? (x > 1 ? 1 : x) : 0);   // NaN -> 0
const smooth = (a: number, b: number, x: number): number => { const t = clamp01((x - a) / (b - a)); return t * t * (3 - 2 * t); };

/** Progress (0..1) of piece `i` of `n` when the whole reveal is at `reveal`. Pieces start one after another across
 *  `stagger` of the timeline and each takes the remaining (1 - stagger) share. reveal=0 -> all 0; reveal=1 -> all 1. */
export function pieceProgress(reveal: number, i: number, n: number, stagger: number): number {
  const r = clamp01(reveal);
  if (r >= 1) return 1;
  if (r <= 0) return 0;
  const s = Math.min(Math.max(stagger, 0), 0.95);
  const start = n > 1 ? (i / (n - 1)) * s : 0;
  return clamp01((r - start) / (1 - s));
}

export interface RevealFx { alpha: number; dy: number; scale: number; rotX: number; blur: number; wipe: number }
/** What a piece looks like at progress u for an effect. dy is in world px (+ = up on screen). wipe < 0 means "no wipe". */
export function revealFx(effect: RevealEffect, u: number, distance: number): RevealFx {
  const e = (kind: Parameters<typeof ease>[0]) => ease(kind, u);
  const base: RevealFx = { alpha: 1, dy: 0, scale: 1, rotX: 0, blur: 0, wipe: -1 };
  if (u >= 1) return base;
  switch (effect) {
    case "fade": return { ...base, alpha: e("ease_out") };
    case "rise": return { ...base, alpha: smooth(0, 0.6, u), dy: -distance * (1 - e("ease_out_expo")) };
    case "drop": return { ...base, alpha: smooth(0, 0.6, u), dy: distance * (1 - e("ease_out_expo")) };
    case "scale": return { ...base, alpha: smooth(0, 0.5, u), scale: 0.4 + 0.6 * e("ease_out_back") };
    case "blur": return { ...base, alpha: smooth(0, 0.7, u), blur: 28 * (1 - e("ease_out")) };
    case "flip": return { ...base, alpha: smooth(0, 0.5, u), rotX: 90 * (1 - e("ease_out_back")) };
    case "wipe": return { ...base, wipe: e("ease_in_out") };
    default: return base;
  }
}

/** Eased progress of a scene transition (u in 0..1), or null when none is running. */
export function transitionProgress(kind: TransitionKind | undefined, durationMs: number, localMs: number): number | null {
  if (!kind || kind === "none" || !(durationMs > 0) || localMs >= durationMs || localMs < 0) return null;
  return localMs / durationMs;
}

export const TRANSITION_INDEX: Record<TransitionKind, number> = {
  none: 0, fade: 1, slide_left: 2, slide_right: 3, slide_up: 4, slide_down: 5, push_left: 6, push_up: 7,
  zoom_in: 8, zoom_out: 9, wipe_left: 10, wipe_up: 11, iris: 12, blur: 13, glitch: 14,
};

/** Number of gaussian-blur levels a bloom radius needs (more radius = a wider, softer glow). */
export const bloomLevels = (radius: number): number => (radius > 1.4 ? 3 : 2);
